// PostHog analytics — lazy-loaded so it never blocks the app.
// Only userId (UUID) is sent; no names, emails, or other PII.

let _ph = null;

export async function initAnalytics() {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      api_host: 'https://app.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      autocapture: false,
      // Automatically capture JS exceptions — uses source maps from public CDN
      // to symbolicate minified stack traces in the PostHog error tracking UI.
      autocapture_exceptions: true,
      persistence: 'localStorage',
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: { password: true },
      },
    });
    _ph = posthog;
  } catch {
    // PostHog load failure is non-fatal
  }
}

export function identifyUser(userId) {
  if (!_ph || !userId) return;
  _ph.identify(userId); // UUID only — no name or email
}

export function trackEvent(name, properties = {}) {
  if (!_ph) return;
  _ph.capture(name, properties);
}

/**
 * Capture a caught exception to PostHog error tracking.
 * Safe to call before PostHog is initialised — silently no-ops.
 *
 * @param {Error|unknown} error
 * @param {string}        context  - where the error was caught (e.g. 'window.onerror')
 * @param {object}        extra    - any additional properties to include
 */
export function captureException(error, context = 'unknown', extra = {}) {
  if (!_ph) return;
  const isNetwork = /load failed|failed to fetch|networkerror/i.test(
    error?.message ?? '',
  );
  if (isNetwork) return; // network failures are the user's connection, not our bugs

  _ph.capture('$exception', {
    $exception_message:    error?.message    ?? String(error),
    $exception_type:       error?.name       ?? 'Error',
    $exception_stack_trace_raw: error?.stack ?? '',
    context,
    $current_url: window?.location?.href ?? '',
    ...extra,
  });
}

export function optOut() { if (_ph) _ph.opt_out_capturing(); }
export function optIn()  { if (_ph) _ph.opt_in_capturing();  }
